// src/pages/ArticleSearchScreen.jsx

import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosClient from '../api/axiosClient';

// Helper function to format the ISO date string
const formatDate = (isoString) => {
  const date = new Date(isoString);
  // Adjust options as needed for desired display
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const fetchArticles = async (query, publishedAtWindow, topic) => {
  const params = { query };
  if (publishedAtWindow) params.published_at_window = publishedAtWindow;
  if (topic) params.topic = topic;
  const { data } = await axiosClient.get('/search/balanced', { params });
  return data;
};

const ArticleSearchScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialPublishedWindow = searchParams.get('published_at_window') || '';
  const initialTopic = searchParams.get('topic') || '';

  const [query, setQuery] = useState(initialQuery);
  const [publishedAtWindow, setPublishedAtWindow] = useState(initialPublishedWindow);
  const [topic, setTopic] = useState(initialTopic);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [debouncedPublishedAtWindow, setDebouncedPublishedAtWindow] = useState(initialPublishedWindow);
  const [debouncedTopic, setDebouncedTopic] = useState(initialTopic);

  const navigate = useNavigate();

  // Debounce input: update debounced values & URL after 500ms of no changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
      setDebouncedPublishedAtWindow(publishedAtWindow);
      setDebouncedTopic(topic);
      setSearchParams({
        q: query,
        published_at_window: publishedAtWindow,
        topic: topic,
      });
    }, 500);
    return () => clearTimeout(handler);
  }, [query, publishedAtWindow, topic, setSearchParams]);

  const { data: articles, isLoading, error } = useQuery(
    ['searchArticles', debouncedQuery, debouncedPublishedAtWindow, debouncedTopic],
    () => fetchArticles(debouncedQuery, debouncedPublishedAtWindow, debouncedTopic),
    {
      enabled: debouncedQuery.trim() !== '',
      staleTime: 300000, // Cache for 5 minutes
    }
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto p-6">
        <h1 className="text-3xl font-bold text-green-700 mb-4">Search Articles</h1>
        <form onSubmit={(e) => e.preventDefault()} className="mb-6 space-y-4">
          <input
            type="text"
            placeholder="Enter your search query..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full p-3 border border-green-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <div className="flex flex-col sm:flex-row sm:space-x-4">
            <input
              type="number"
              placeholder="Published in last (days)"
              value={publishedAtWindow}
              onChange={(e) => setPublishedAtWindow(e.target.value)}
              className="w-full p-3 border border-green-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <input
              type="text"
              placeholder="Topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full p-3 border border-green-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </form>
        {isLoading && <p className="text-center text-green-600">Loading...</p>}
        {error && <p className="text-center text-red-500">Error fetching articles</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {articles &&
            articles.map((article) => (
              <div
                key={article.doc_reference}
                className="p-4 border border-green-200 rounded-lg shadow hover:shadow-xl cursor-pointer transition-all"
                onClick={() =>
                  navigate(`/article/${article.doc_reference}`, {
                    state: { query: debouncedQuery },
                  })
                }
              >
                <h3 className="text-xl font-semibold text-green-800">{article.title}</h3>
                <p className="mt-2 text-gray-700">{article.description}</p>
                {article.published_at && (
                  <p className="mt-1 text-sm text-gray-500">
                    {formatDate(article.published_at)}
                  </p>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ArticleSearchScreen;
